import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { LogIn, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BG =
    "https://static.prod-images.emergentagent.com/jobs/1f681ca5-b54b-40cf-9539-84fe176a321c/images/99f90e80d87c21b1d662712e41e700007b1084456f909d654e35b72e64549b47.png";

export default function AdminLogin() {
    const { user, loading, login, formatApiError } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    if (loading) return null;
    if (user && user !== false) return <Navigate to="/admin" replace />;

    async function submit(e) {
        e.preventDefault();
        setErr(null);
        setSubmitting(true);
        try {
            await login(email.trim(), password.trim());
            navigate("/admin", { replace: true });
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="min-h-screen relative grid lg:grid-cols-2">
            {/* Left brand panel */}
            <div className="hidden lg:flex relative overflow-hidden bg-slate-950 text-white p-12 flex-col justify-between">
                <img
                    src={BG}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-40 mix-blend-screen"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/70 to-slate-950" />
                <div className="relative z-10">
                    <Link to="/" data-testid="admin-back-home" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                        <ArrowLeft className="w-4 h-4" /> Voltar ao site
                    </Link>
                </div>
                <div className="relative z-10">
                    <span className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--tp-orange)]">
                        Backoffice
                    </span>
                    <h1 className="mt-4 text-5xl font-black tracking-tight leading-none">
                        TRACKING<br />
                        <span className="text-[var(--tp-orange)]">PLUS</span>
                    </h1>
                    <p className="mt-6 text-slate-300 max-w-md">
                        Gestão centralizada de envios para a equipa LOGISTICA PLUS. Acesso
                        restrito a administradores autorizados.
                    </p>
                </div>
                <div className="relative z-10 font-mono text-xs text-slate-400 uppercase tracking-widest">
                    Secure access · {new Date().getFullYear()}
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
                <form
                    onSubmit={submit}
                    className="w-full max-w-md"
                    data-testid="admin-login-form"
                >
                    <div className="lg:hidden mb-8">
                        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-600">
                            <ArrowLeft className="w-4 h-4" /> Voltar
                        </Link>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
                        Acesso administrativo
                    </span>
                    <h2 className="mt-2 text-3xl font-black tracking-tight">
                        Entrar no backoffice
                    </h2>
                    <p className="mt-2 text-slate-600">
                        Insira as suas credenciais para continuar.
                    </p>

                    <div className="mt-8 space-y-4">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                                Email
                            </label>
                            <input
                                data-testid="admin-login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                placeholder="admin@trackingplus.pt"
                                className="w-full h-12 px-4 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">
                                Palavra-passe
                            </label>
                            <input
                                data-testid="admin-login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                placeholder="••••••••"
                                className="w-full h-12 px-4 border border-slate-300 focus:border-[var(--tp-blue)] focus:ring-1 focus:ring-[var(--tp-blue)] outline-none bg-white"
                            />
                        </div>
                    </div>

                    {err && (
                        <div
                            data-testid="admin-login-error"
                            className="mt-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                        >
                            {err}
                        </div>
                    )}

                    <button
                        data-testid="admin-login-button"
                        type="submit"
                        disabled={submitting}
                        className="mt-6 w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-all duration-300 hover:-translate-y-0.5"
                    >
                        <LogIn className="w-4 h-4" />
                        {submitting ? "A entrar..." : "Entrar"}
                    </button>
                </form>
            </div>
        </div>
    );
}
